// Wire contracts match PirdyUsbDeviceManager and CYD experience firmware.
const integer = (value, minimum, maximum, name) => {
  const number = Number(value);
  if (!Number.isInteger(number) || number < minimum || number > maximum) throw new Error(`Choose a valid ${name}.`);
  return number;
};
export function timeMinutes(value) {
  if (!/^\d{2}:\d{2}$/.test(value ?? "")) throw new Error("Choose a valid time.");
  const [hours, minutes] = value.split(":").map(Number);
  return integer(hours, 0, 23, "hour") * 60 + integer(minutes, 0, 59, "minute");
}
export function scheduleWindow(enabled, days, start, end) {
  const result = { enabled: Boolean(enabled), days: integer(days, 1, 127, "weekday selection"), start: timeMinutes(start), end: timeMinutes(end) };
  if (result.start === result.end) throw new Error("Choose different start and end times.");
  return result;
}
export function experienceCommand(state, action, values = {}) {
  if (!state || state.pending) throw new Error("Refresh the device and wait for its current command to finish.");
  const requireFeature = feature => { if (!state.features?.[feature]) throw new Error("This option is unavailable on the connected board or firmware."); };
  switch (action) {
    case "mute":
      if (state.features?.audioMute !== true) throw new Error("Mute control requires supported Pirdy firmware.");
      if (state.speakerAvailable !== true || typeof values.muted !== "boolean") throw new Error("Choose Mute or Unmute on a supported Pirdy.");
      return { action, muted: values.muted };
    case "sleepTimer":
      requireFeature("independentSleepTimer");
      return { action, brightness: state.brightness, volume: state.volume, resumeSeconds: state.resumeSeconds,
        sleepMinutes: integer(values.sleepMinutes, 0, 180, "sleep timer") };
    case "locate":
      if (!state.locatorAvailable) throw new Error("Board identification is unavailable.");
      return { action, brightness: state.brightness, volume: state.volume, resumeSeconds: state.resumeSeconds, sleepMinutes: state.sleepMinutes };
    case "automation":
      requireFeature("displaySchedules");
      return { action, offsetMinutes: integer(values.offsetMinutes, -720, 840, "UTC offset"),
        displayWindow: scheduleWindow(values.displayEnabled, values.displayDays, values.displayStart, values.displayEnd),
        quietWindow: scheduleWindow(values.quietEnabled, values.quietDays, values.quietStart, values.quietEnd) };
    case "dashboard":
      requireFeature("clockDashboard");
      return { action, mode: integer(values.mode, 0, 2, "dashboard"), alarmEnabled: Boolean(values.alarmEnabled),
        alarmDays: integer(values.alarmDays, 1, 127, "alarm days"), alarmMinute: timeMinutes(values.alarmTime),
        alarmSha256: state.dashboard?.alarmSha256 ?? "" };
    case "eventLogging": case "clearEvents":
      requireFeature("interactionLogging");return { action, enabled: action === "clearEvents" ? Boolean(state.events?.enabled) : Boolean(values.enabled) };
    case "library": {
      requireFeature("offlineLibrary");
      const { libraryAction, id = "", name = "" } = values;
      if (!["save", "remove", "favourite", "activate"].includes(libraryAction)) throw new Error("Choose a library action.");
      if (libraryAction === "save") {
        if (!name.trim() || name.length > 40 || /[\x00-\x1f\x7f]/.test(name)) throw new Error("Enter a playlist name of up to 40 characters.");
      } else if (!state.library?.entries?.some(entry => entry.id === id)) throw new Error("Select a saved playlist from this board.");
      return { action, libraryAction, id, name: name.trim() };
    }
    default: throw new Error("Unknown device option.");
  }
}
export function confirmExperienceCommand(before, command, after) {
  const fail = () => { throw new Error("The device has not confirmed this change. Refresh before retrying."); };
  if (!after || after.pending || after.error) fail();
  if (command.action === "mute") {
    if (after.features?.audioMute !== true || after.speakerAvailable !== true || after.muted !== command.muted ||
        !Number.isInteger(after.volume) || after.volume < 0 || after.volume > 3 || after.muted !== (after.volume === 0) ||
        !Number.isInteger(after.unmuteVolume) || after.unmuteVolume < 1 || after.unmuteVolume > 3) fail();
  } else if (command.action === "automation") {
    const data = after.automation;
    if (!data || data.error || data.pending || data.offsetMinutes !== command.offsetMinutes) fail();
    for (const name of ["displayWindow", "quietWindow"])
      for (const key of ["enabled", "days", "start", "end"]) if (data[name]?.[key] !== command[name][key]) fail();
  } else if (command.action === "dashboard") {
    if (after.dashboard?.error || after.dashboard?.pending) fail();
    for (const key of ["mode", "alarmEnabled", "alarmDays", "alarmMinute", "alarmSha256"])
      if (after.dashboard?.[key] !== command[key]) fail();
  } else if (["eventLogging", "clearEvents"].includes(command.action)) {
    if (!after.events || after.events.pending || after.events.error) fail();
    if (command.action === "eventLogging" ? after.events.enabled !== command.enabled : after.events.buffered !== 0 || after.events.dropped !== 0) fail();
  } else if (command.action === "sleepTimer" && after.sleepMinutes !== command.sleepMinutes) fail();
  else if (command.action === "library") {
    const library = after.library;
    if (!library || library.pending || library.error) fail();
    const entry = library.entries?.find(value => value.id === command.id);
    if (command.libraryAction === "activate" && library.lastActivatedId !== command.id) fail();
    if (command.libraryAction === "remove" && entry) fail();
    if (command.libraryAction === "favourite" && (!entry || entry.favourite === before.library.entries.find(value => value.id === command.id)?.favourite)) fail();
    if (command.libraryAction === "save" && !library.entries?.some(value => value.name === command.name && !before.library.entries.some(old => old.id === value.id))) fail();
  }
  return after;
}
